import { ApolloError } from '@apollo/client';

import {
  useFakturowniaInvoiceAggregatesQuery,
  FakturowniaInvoiceAggregatesQuery,
} from 'core/graphql';

import { isPermissionDeniedError } from './errors';

export type IInvoiceAggregateRow = NonNullable<
  NonNullable<
    FakturowniaInvoiceAggregatesQuery['fakturowniaInvoiceAggregates']
  >[number]
>;

interface IInvoiceAggregatesArgs {
  direction?: string;
  studentId?: string;
  groupId?: string;
  dateFrom?: string;
  dateTo?: string;
  groupBy: 'none' | 'currency' | 'month' | 'group';
  skip?: boolean;
}

interface IInvoiceAggregatesHook {
  rows: IInvoiceAggregateRow[];
  loading: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

/**
 * Server-side invoice aggregates (Phase 3, backend TZ §2). One call replaces
 * the old `per_page=500` + client `sum()` pattern: Dashboard KPI, Group
 * Finances and any future per-period revenue view consume this.
 */
export const useFakturowniaInvoiceAggregates = ({
  direction,
  studentId,
  groupId,
  dateFrom,
  dateTo,
  groupBy,
  skip = false,
}: IInvoiceAggregatesArgs): IInvoiceAggregatesHook => {
  const {
    data,
    loading,
    error,
    refetch,
  } = useFakturowniaInvoiceAggregatesQuery({
    variables: {
      direction: direction || undefined,
      studentId: studentId || undefined,
      groupId: groupId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      groupBy,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const rows = (
    data?.fakturowniaInvoiceAggregates || []
  ).filter((row): row is IInvoiceAggregateRow => Boolean(row));

  return {
    rows,
    loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch: () => {
      refetch();
    },
  };
};
